// FaturasJsPdfProvider.tsx
import React, { useEffect, useState } from 'react';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ====== TIPOS BÁSICOS ======

interface ReportPayload {
    nameReport: string;
    dataset: any[]; // você pode tipar melhor depois
    data: {
        phone: string;
        email: string;
        address: string;
        pathImage?: string | null;
        localeSigleOne: string;
        localeSigleTwo: string;
        city: string;
        nameCompany: string;
        balanceTotal: string;
    };
}

type Fatura = {
    data: string;
    pagamento: string;
    cliente: string;
    descricao: string;
    receber: number;
    pagar: number;
    saldo: number;
};

type JsPdfProviderProps = {
    payload: ReportPayload;
    children: (args: {
        loading: boolean;
        error: Error | null;
        blob: Blob | null;
        url: string | null;
    }) => React.ReactNode;
};

// ====== HELPERS GERAIS ======
const formatValor = (v: number): string =>
    v.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    });

// ====== GERAÇÃO DO PDF (JASPER-LIKE) ======

/**
 * Gera o PDF de faturas com:
 * - header azul só na primeira página
 * - thead em todas as páginas
 * - footer fixo por coordenada (Y)
 * - totais acumulados por página
 * - paginação controlada por Y (como Jasper)
 */
export const generateFaturasPdf = (
    payload: ReportPayload,
    options?: { includeSaldo?: boolean },
): jsPDF => {
    const includeSaldo = options?.includeSaldo ?? false;
    const doc = new jsPDF('p', 'mm', 'a4');

    // Constantes de layout (em mm)
    const PAGE_WIDTH = 210;
    const PAGE_HEIGHT = 297;
    const MARGIN_LEFT = 10;
    const MARGIN_RIGHT = 10;
    const MARGIN_TOP = 10;
    const MARGIN_BOTTOM = 10;

    const HEADER_HEIGHT = 20; // altura do header azul
    const TABLE_HEADER_HEIGHT = 8; // linha de cabeçalho da tabela
    const FOOTER_RESERVED_HEIGHT = 20; // espaço reservado (totais + barra inferior)
    const LINE_HEIGHT = 4; // altura básica de uma linha de texto na tabela

    // Larguras das colunas (somando 190 = 210 - 20 de margem)
    const COL_DATA_W = includeSaldo ? 16 : 18;
    const COL_PAGTO_W = includeSaldo ? 24 : 28;
    const COL_CLIENTE_W = includeSaldo ? 35 : 40;
    const COL_DESC_W = includeSaldo ? 45 : 54;
    const COL_RECEBER_W = includeSaldo ? 22 : 25;
    const COL_PAGAR_W = includeSaldo ? 22 : 25;
    const COL_SALDO_W = includeSaldo ? 26 : 0;

    const xData = MARGIN_LEFT;
    const xPagto = xData + COL_DATA_W;
    const xCliente = xPagto + COL_PAGTO_W;
    const xDesc = xCliente + COL_CLIENTE_W;
    const xReceber = xDesc + COL_DESC_W;
    const xPagar = xReceber + COL_RECEBER_W;
    const xSaldo = xPagar + COL_PAGAR_W;

    const toNumber = (value: any): number =>
        typeof value === 'number' ? value : Number(value) || 0;

    // Monta faturas a partir do payload
    const faturas: Fatura[] = payload.dataset.map((item: any) => ({
        data: item.dueDate,
        pagamento: item.paymentDate,
        cliente: item.customer?.name ?? '',
        descricao: item.origins ?? item.description ?? '',
        receber: toNumber(item.receivedForSum || item.received),
        pagar: toNumber(item.payableForSum || item.payable),
        saldo: toNumber(item.balance),
    }));

    // Função para desenhar header azul (só 1ª página)
    const drawBlueHeader = () => {
        doc.setFillColor(0, 102, 153);
        doc.rect(0, 0, PAGE_WIDTH, HEADER_HEIGHT, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Faturas', MARGIN_LEFT, 12);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(
            'Relatório de contas a pagar e a receber',
            PAGE_WIDTH - MARGIN_RIGHT - 70,
            12,
            { align: 'left' },
        );
    };

    // Função para desenhar o cabeçalho da tabela
    const drawTableHeader = (isFirstPage: boolean): number => {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 102, 153);
        doc.setFillColor(230, 230, 230);

        let startY =
            MARGIN_TOP +
            (isFirstPage ? HEADER_HEIGHT : 0) + // se for primeira, pula o header azul
            5;

        if (isFirstPage) {
            startY = startY - MARGIN_TOP;
        }

        doc.rect(
            xData,
            startY - 4,
            PAGE_WIDTH - MARGIN_RIGHT - MARGIN_LEFT,
            LINE_HEIGHT + 2,
            'F',
        );
        let y = startY;

        doc.text('Data', xData, y);
        doc.text('Pagamento', xPagto, y);
        doc.text('Cliente', xCliente, y);
        doc.text('Desc./Origem', xDesc, y);
        doc.text('À receber', xReceber, y);
        doc.text('À pagar', xPagar, y);
        if (includeSaldo) {
            doc.text('Saldo', xSaldo, y);
        }

        // linha horizontal embaixo do header da tabela
        y += 2;
        // doc.setDrawColor(0);
        doc.line(MARGIN_LEFT, y, PAGE_WIDTH - MARGIN_RIGHT, y);

        return y; // retorna Y de início das linhas de dados
    };

    // Função para desenhar footer (totais + barra inferior cinza)
    const drawFooterForPage = (
        pageNumber: number,
        totalPages: number,
        acumuladoReceber: number,
        acumuladoPagar: number,
    ) => {
        const footerYTop =
            PAGE_HEIGHT - MARGIN_BOTTOM - FOOTER_RESERVED_HEIGHT + 5;
        const bottomBarY = PAGE_HEIGHT - MARGIN_BOTTOM - 5;
        const totalSaldo = acumuladoReceber - acumuladoPagar;

        // Totais (sem fundo, alinhados à direita)
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);

        // acumuladoReceber = 200000000;
        // acumuladoPagar = 200000000;
        const label = 'Totais:';
        const labelWidth = doc.getTextWidth(label);
        const totalValues = includeSaldo
            ? [acumuladoReceber, acumuladoPagar, totalSaldo]
            : [acumuladoReceber, acumuladoPagar];
        const valueWidth = Math.max(
            ...totalValues.map(value => doc.getTextWidth(formatValor(value))),
        );

        // const xLabel =
        //     PAGE_WIDTH - MARGIN_RIGHT - (labelWidth + 2 + valueWidth * 2 + 10);
        const xLabel = xReceber - 15;
        // const xValor1 = xLabel + labelWidth + 4;
        let xValor1 = xReceber;
        // let xValor2 = xValor1 + valueWidth + 6;
        let xValor2 = xPagar;
        let xValor3 = xSaldo;
        if (valueWidth >= 25) {
            xValor1 = xLabel + labelWidth + 2;
            xValor2 = xValor1 + valueWidth + 2;
            if (includeSaldo) {
                xValor3 = xValor2 + valueWidth + 2;
            }
        }

        doc.text(label, xLabel, footerYTop);
        doc.setFont('helvetica', 'normal');
        doc.text(formatValor(acumuladoReceber), xValor1, footerYTop, {
            align: 'left',
        });
        doc.text(formatValor(acumuladoPagar), xValor2, footerYTop, {
            align: 'left',
        });
        if (includeSaldo) {
            doc.text(formatValor(totalSaldo), xValor3, footerYTop, {
                align: 'left',
            });
        }

        // Barra inferior cinza com cidade + Página X de Y
        doc.setFillColor(240, 240, 240);
        doc.rect(
            MARGIN_LEFT,
            bottomBarY,
            PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT,
            7,
            'F',
        );

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const dateComplet = format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", {
            locale: ptBR,
        });
        doc.text(dateComplet, MARGIN_LEFT + 2, bottomBarY + 4);

        const paginaText = `Página ${pageNumber} de ${totalPages}`;
        doc.text(paginaText, PAGE_WIDTH - MARGIN_RIGHT - 2, bottomBarY + 4, {
            align: 'right',
        });
    };

    // ======= LAYOUT DAS PÁGINAS (CONTEÚDO) =======

    let pageNumber = 1;
    let y = 0;
    let acumuladoReceber = 0;
    let acumuladoPagar = 0;

    // vamos guardar o acumulado POR PÁGINA para desenhar o footer depois
    const pageInfos: {
        pageNumber: number;
        acumuladoReceber: number;
        acumuladoPagar: number;
    }[] = [];

    // primeira página: header azul + cabeçalho da tabela
    drawBlueHeader();
    y = drawTableHeader(true);

    const contentBottomY =
        PAGE_HEIGHT - MARGIN_BOTTOM - FOOTER_RESERVED_HEIGHT - 2;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    // percorre as faturas desenhando linha a linha
    faturas.forEach((fatura, index) => {
        const { data, pagamento, cliente, descricao, receber, pagar, saldo } =
            fatura;

        // quebra de texto para Cliente e Desc./Origem
        const clienteLines = doc.splitTextToSize(cliente || '', COL_CLIENTE_W);
        const descLines = doc.splitTextToSize(descricao || '', COL_DESC_W);

        const lineCount = Math.max(
            clienteLines.length || 1,
            descLines.length || 1,
            1,
        );

        const rowHeight = lineCount * LINE_HEIGHT + 1;

        // se não cabe nesta página, fecha página atual e abre outra
        if (y + rowHeight > contentBottomY) {
            // registra acumulado até essa página
            pageInfos.push({
                pageNumber,
                acumuladoReceber,
                acumuladoPagar,
            });

            // nova página
            doc.addPage();
            pageNumber += 1;

            // na nova página NÃO desenhamos header azul,
            // apenas o cabeçalho da tabela
            y = drawTableHeader(false);

            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
        }

        // número de linhas por coluna
        const linesData = 1;
        const linesPagto = 1;
        const linesCliente = clienteLines.length;
        const linesDesc = descLines.length;
        const linesReceber = 1;
        const linesPagar = 1;
        const linesSaldo = includeSaldo ? 1 : 0;

        // função auxiliar para centralizar verticalmente
        const centerY = (lines: number) => {
            const fullHeight = lineCount * LINE_HEIGHT;
            const thisHeight = lines * LINE_HEIGHT;
            const offset = (fullHeight - thisHeight) / 2;
            return y + offset + LINE_HEIGHT; // baseline da primeira linha
        };

        // ===== Desenho centralizado =====

        doc.text(data || '', xData, centerY(linesData));
        doc.text(pagamento || '', xPagto, centerY(linesPagto));

        doc.text(clienteLines, xCliente, centerY(linesCliente));
        doc.text(descLines, xDesc, centerY(linesDesc));

        doc.text(formatValor(receber), xReceber, centerY(linesReceber));
        doc.text(formatValor(pagar), xPagar, centerY(linesPagar));
        if (includeSaldo) {
            doc.text(formatValor(saldo), xSaldo, centerY(linesSaldo));
        }

        // linha horizontal embaixo da fatura
        const yLine = y + rowHeight;
        doc.line(MARGIN_LEFT, yLine, PAGE_WIDTH - MARGIN_RIGHT, yLine);

        // atualiza Y para próxima linha
        y = yLine;

        // totais acumulados
        acumuladoReceber += receber;
        acumuladoPagar += pagar;
    });

    // registra info da última página
    pageInfos.push({
        pageNumber,
        acumuladoReceber,
        acumuladoPagar,
    });

    // ======= SEGUNDA PASSAGEM: DESENHAR FOOTERS EM TODAS AS PÁGINAS =======

    // jsPDF expõe getNumberOfPages() na instância
    const totalPages =
        (doc as any).getNumberOfPages?.() ??
        (doc.internal as any).pages?.length ??
        1;

    for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        const info = pageInfos[p - 1] || pageInfos[pageInfos.length - 1];
        drawFooterForPage(
            p,
            totalPages,
            info.acumuladoReceber,
            info.acumuladoPagar,
        );
    }

    return doc;
};

// ====== PROVIDER TIPO "BlobProvider" ======

export const JsPdfProvider: React.FC<JsPdfProviderProps> = ({
    payload,
    children,
}) => {
    const [blob, setBlob] = useState<Blob | null>(null);
    const [url, setUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let cancelled = false;
        let oldUrl: string | null = null;

        const generate = async () => {
            setLoading(true);
            setError(null);

            try {
                const doc = generateFaturasPdf(payload);
                const pdfBlob = doc.output('blob');

                if (cancelled) return;

                if (oldUrl) {
                    URL.revokeObjectURL(oldUrl);
                }

                const pdfUrl = URL.createObjectURL(pdfBlob);
                oldUrl = pdfUrl;

                setBlob(pdfBlob);
                setUrl(pdfUrl);
            } catch (e: any) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        generate();

        return () => {
            cancelled = true;
            if (oldUrl) {
                URL.revokeObjectURL(oldUrl);
            }
        };
    }, [payload]);

    return <>{children({ loading, error, blob, url })}</>;
};

// ====== EXEMPLO DE USO ======
// (pode estar no mesmo arquivo ou em outro componente)

/*
import React from "react";
import JsPdfProvider from "./FaturasJsPdfProvider";
import { payload } from "./seuPayload";

const FaturasPdfPreview: React.FC = () => {
  return (
    <JsPdfProvider payload={payload}>
      {({ loading, error, url }) => {
        if (loading) return <span>Gerando PDF...</span>;
        if (error) return <span>Erro ao gerar PDF: {error.message}</span>;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <a href={url ?? undefined} download={`${payload.nameReport}.pdf`}>
              Baixar PDF
            </a>

            {url && (
              <iframe
                src={url}
                style={{ width: "100%", height: 600, border: "1px solid #ccc" }}
                title="Preview Faturas"
              />
            )}
          </div>
        );
      }}
    </JsPdfProvider>
  );
};

export default FaturasPdfPreview;
*/
